import { Button, Card, Descriptions, Form, Input, Message, Modal, Popconfirm, Space, Table, Tag, Upload } from "@arco-design/web-react"
import OperationButton from "@/components/OperationButton";
import { WarehouseAPI } from "@/apis/material";
import { EnumLabel, EnumSelect } from "@/components/Enum";
import { StatusDef, InventoryStatusDef, Status, InventoryStatus } from "@/enums/status";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconCheck, IconClose, IconDelete, IconEdit } from "@arco-design/web-react/icon";
import { baseUrl } from "@/utils/api";

const FormItem = Form.Item;
const TextArea = Input.TextArea;

const EditWarehouseButton = ({ value, onOk }) => {
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();

    const submit = async () => {
        try {
            const values = await form.validate();
            await WarehouseAPI.update(values.id, values);
            setVisible(false);
            onOk && onOk();
        } catch (error) {
            Message.error(error.message);
        }
    }

    return (
        <>
            <Button type="primary" onClick={() => {
                setVisible(true);
            }}>Edit</Button>

            <Modal title="Warehouse" visible={visible}
                style={{ width: 550 }}
                afterClose={() => form.resetFields()}
                onCancel={() => setVisible(false)}
                onOk={() => submit()}
            >
                <Form form={form} colon
                    initialValues={value}
                    labelCol={{ span: 9 }}
                    wrapperCol={{ span: 14 }}>
                    <FormItem field="id" hidden>
                        <Input />
                    </FormItem>
                    <FormItem field="warehouseNumber" label="Warehouse Number" required rules={[{ required: true }]}>
                        <Input />
                    </FormItem>
                    <FormItem field="warehouseName" label="Warehouse Name" required rules={[{ required: true }]}>
                        <Input />
                    </FormItem>
                    <FormItem field="inventoryStatus" label="Inventory" required rules={[{ required: true }]}>
                        <EnumSelect enumObj={InventoryStatusDef} />
                    </FormItem>
                    <FormItem field="note" label="Notes">
                        <TextArea />
                    </FormItem>
                </Form>
            </Modal>
        </>
    )
}

const AddLocationButton = (props) => {
    const { warehouseId, warehouseStatus } = props;
    const [warehouseNotAvailable] = useState(warehouseStatus && warehouseStatus === InventoryStatus.NotAvailable)
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const [initialValues] = useState({
        status: warehouseNotAvailable ? InventoryStatus.NotAvailable : InventoryStatus.Available
    })

    const submit = async () => {
        try {
            const values = await form.validate();
            if (values.id) {
                await WarehouseAPI.updateLocation(warehouseId, values.id, values);
                Message.success("Warehouse location updated successfully")
            } else {
                await WarehouseAPI.addLocation(warehouseId, values);
                Message.success("Warehouse location created successfully")
                setVisible(false);
                props.onOk && props.onOk();
            }
        } catch (error) {
            Message.error(error.message);
        }
    }

    useEffect(() => {

    }, []);

    return (
        <>
            <Button type="primary" onClick={() => {
                setVisible(true);
            }}>Add New Location</Button>

            <Modal title="Add New Location" visible={visible}
                afterClose={() => form.resetFields()}
                onCancel={() => setVisible(false)}
                onOk={submit}
            >
                <Form form={form} colon
                    initialValues={initialValues}
                    labelCol={{ span: 8 }} wrapperCol={{ span: 15 }}>
                    <FormItem field="id" hidden>
                        <Input />
                    </FormItem>
                    <FormItem field="name" label="Location" required>
                        <Input />
                    </FormItem>
                    <FormItem field="status" label="Inventory Status" required>
                        <EnumSelect enumObj={InventoryStatusDef} disabled={warehouseNotAvailable} />
                    </FormItem>
                    <FormItem field="notes" label="Notes">
                        <TextArea></TextArea>
                    </FormItem>
                </Form>
            </Modal>
        </>
    );
}

const EditLocationButton = (props) => {
    const { value } = props;
    const [warehouseId] = useState(value.warehouseId);
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();

    const submit = async () => {
        try {
            const values = await form.validate();
            await WarehouseAPI.updateLocation(warehouseId, values.id, values);
            Message.success("Warehouse location updated successfully")
            setVisible(false);
            props.onOk && props.onOk();
        } catch (error) {
            Message.error(error.message);
        }
    }

    useEffect(() => {
        if (value) {
            form.setFieldsValue(value);
        }
    }, [form, value]);

    return (
        <>
            <OperationButton icon={<IconEdit />} label="Edit" onClick={() => {
                setVisible(true);
            }} />

            <Modal title="Edit Location" visible={visible}
                onCancel={() => setVisible(false)}
                onOk={submit}
            >
                <Form form={form} colon
                    labelCol={{ span: 8 }} wrapperCol={{ span: 15 }}>
                    <FormItem field="id" hidden>
                        <Input />
                    </FormItem>
                    <FormItem field="name" label="Location" required>
                        <Input />
                    </FormItem>
                    <FormItem field="status" label="Inventory Status" required>
                        <EnumSelect enumObj={InventoryStatusDef} />
                    </FormItem>
                    <FormItem field="notes" label="Notes">
                        <TextArea></TextArea>
                    </FormItem>
                </Form>
            </Modal>
        </>
    );
}

export const WarehouseInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [warehouse, setWarehouse] = useState({});
    const [warehouseStatus, setWarehouseStatus] = useState();
    const warehouseId = params.warehouseId;

    const [locations, setLocations] = useState([]);
    const [totalLocations, setTotalLocations] = useState(0);
    const [pageLocation, setPageLocation] = useState(1);

    const getWarehouse = useCallback(async () => {
        const value = await WarehouseAPI.get(warehouseId);
        setWarehouse(value);
        setWarehouseStatus(value.inventoryStatus);
    }, [warehouseId]);

    const getLocations = useCallback(async () => {
        const value = await WarehouseAPI.getLocations(warehouseId, pageLocation);
        setLocations(value.records);
        setTotalLocations(value.total);
    }, [warehouseId, pageLocation]);

    const onUploadLocationsChange = (fileList, file) => {
        if (file.status === "done") {
            Message.success("Upload locations successfully");
            getLocations();
        }
    }

    const back = () => {
        navigate("/workspace/settings/warehouse");
    }

    useEffect(() => {
        getWarehouse();
    }, [getWarehouse]);

    useEffect(() => {
        getLocations();
    }, [getLocations]);

    return (
        <Card title={<Space>
            {warehouse.warehouseName}
            <Tag><EnumLabel enumObj={StatusDef} value={warehouse.status} /></Tag>
        </Space>} extra={
            <Space>
                <EditWarehouseButton value={warehouse} onOk={() => getWarehouse()} />
                {warehouse.status === Status.Active &&
                    <Popconfirm title="Are you sure you want to disable this warehouse?"
                        onOk={async () => {
                            try {
                                await WarehouseAPI.disable(warehouseId);
                                Message.success("Warehouse disabled");
                                getWarehouse();
                            } catch (error) {
                                Message.error(error.message);
                            }
                        }}>
                        <Button type="primary" status="danger" icon={<IconClose />}>Disable</Button>
                    </Popconfirm>
                }
                {warehouse.status === Status.Inactive &&
                    <Popconfirm title="Are you sure you want to enable this warehouse?"
                        onOk={async () => {
                            try {
                                await WarehouseAPI.enable(warehouseId);
                                Message.success("Warehouse enabled");
                                getWarehouse();
                            } catch (error) {
                                Message.error(error.message);
                            }
                        }}>
                        <Button type="primary" status="success" icon={<IconCheck />}>Enable</Button>
                    </Popconfirm>
                }
                {warehouseStatus &&
                    <AddLocationButton warehouseId={warehouseId} warehouseStatus={warehouseStatus} onOk={() => getLocations()} />
                }
                <Upload withCredentials
                    headers={{ 'bee-access-token': localStorage.getItem("login.token") }}
                    action={`${baseUrl}/warehouse/${warehouseId}/locations`}
                    onChange={onUploadLocationsChange}
                    showUploadList={false}>
                    <Button type="primary">Import Locations</Button>
                </Upload>
                <Button onClick={() => window.location = `${baseUrl}/WarehouseLocationTemplate.xlsx`}>Download Location Template</Button>
                <Button onClick={() => back()}>Back</Button>
            </Space>
        }>
            <Descriptions colon=": " data={[{
                label: "Warehouse Number",
                value: warehouse.warehouseNumber
            }, {
                label: "Warehouse Name",
                value: warehouse.warehouseName
            }, {
                label: "Inventory Status",
                value: <EnumLabel enumObj={InventoryStatusDef} value={warehouse.inventoryStatus} />
            }, {
                label: "Notes",
                value: warehouse.note
            }]} />

            <Table rowKey="id" columns={[
                { title: "Location", dataIndex: "name" },
                { title: "Inventory Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={InventoryStatusDef} value={record.status} /> },
                { title: "Notes", dataIndex: "notes" },
                {
                    title: "Operations", render: (col, record) => <Space>
                        <EditLocationButton value={record} onOk={() => getLocations()} />
                        <OperationButton icon={<IconDelete />} label="Delete" status="danger" />
                    </Space>
                }
            ]}
                data={locations} pagination={{ total: totalLocations, current: pageLocation, pageSize: 20 }} onChange={(pagination) => { setPageLocation(pagination.current) }}
            />

        </Card >
    )
}